import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import ModalShell from "../modal/ModalShell";
import { AdminUser, adminUserDisplayName } from "../../features/admin/models/AdminUser";

interface ComplimentaryModalProps {
  user: AdminUser;
  busy: boolean;
  /** Applies the choice: an ISO UTC instant to grant until, or null to revoke. */
  onApply: (until: string | null) => void;
  onClose: () => void;
}

const PRESET_MONTHS = [1, 3, 6, 12];

const addMonthsFromNow = (months: number): Date => {
  const date = new Date();
  date.setMonth(date.getMonth() + months);
  return date;
};

const toDateInputValue = (date: Date): string => date.toISOString().slice(0, 10);

const formatDate = (date: Date): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);

/**
 * Grants or revokes complimentary free access for an instructor — beta testers
 * stay Active without ever ordering or paying. Presets fill the date; the grant
 * always ends at the chosen day's end (UTC).
 */
const ComplimentaryModal: React.FC<ComplimentaryModalProps> = ({ user, busy, onApply, onClose }) => {
  const { t } = useTranslation();
  const [until, setUntil] = useState<string>(
    toDateInputValue(user.complimentaryUntil ?? addMonthsFromNow(3))
  );

  const grant = () => {
    // End of the chosen day, UTC — so "until July 31" includes July 31.
    onApply(new Date(`${until}T23:59:59Z`).toISOString());
  };

  return (
    <ModalShell onClose={onClose} labelledBy="complimentary-title">
      <div className="p-6">
        <h2 id="complimentary-title" className="text-lg font-bold text-grey-20 dark:text-white">
          {t("admin.complimentary.title")}
        </h2>
        <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
          {t("admin.complimentary.subtitle", { name: adminUserDisplayName(user) })}
        </p>

        {user.complimentaryUntil && (
          <p className="mt-3 rounded-lg bg-primary-95 px-3 py-2 text-sm text-primary-40 dark:bg-primary-30 dark:text-primary-90">
            {t("admin.complimentary.current", { date: formatDate(user.complimentaryUntil) })}
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {PRESET_MONTHS.map((months) => (
            <button
              key={months}
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={() => setUntil(toDateInputValue(addMonthsFromNow(months)))}
            >
              {t("admin.complimentary.months", { count: months })}
            </button>
          ))}
        </div>

        <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
          {t("admin.complimentary.untilLabel")}
          <input
            type="date"
            className="input mt-1"
            value={until}
            min={toDateInputValue(new Date())}
            onChange={(event) => setUntil(event.target.value)}
          />
        </label>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          {user.complimentaryUntil ? (
            <button
              type="button"
              className="text-sm font-medium text-red-700 hover:underline dark:text-red-400"
              disabled={busy}
              onClick={() => onApply(null)}
            >
              {t("admin.complimentary.revoke")}
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>
              {t("common.cancel")}
            </button>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={grant}>
              {t("admin.complimentary.grant")}
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default ComplimentaryModal;
